import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../constants/api';

const APP_STORE_URL = 'https://apps.apple.com/app/spotto/id6740039498';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=pl.spotto.mobile';
const APP_SCHEME = 'spottospace';
const APP_ID = '6740039498';

type GroupActivity = {
  id: string;
  title: string;
  sportType: string;
  locationText: string;
  date: string;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  confirmedCount: number;
  status: 'Active' | 'Cancelled';
};

// Helper to update or create meta tag
const setMetaTag = (property: string, content: string, isName = false) => {
  const attr = isName ? 'name' : 'property';
  let element = document.querySelector(`meta[${attr}="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, property);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' });
};

// Public deep-link landing page for a Sparing (GroupActivity) — the "app not
// installed" fallback for the /sparing/:activityId universal/app link
// (see KAN-319). Mirrors ProductDeepLink.tsx/BookBusiness.tsx.
export const GroupActivityDeepLink: React.FC = () => {
  const { activityId } = useParams<{ activityId: string }>();
  const { t } = useTranslation();
  const [activity, setActivity] = useState<GroupActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  useEffect(() => {
    if (activity) {
      document.title = `${activity.title} - Spotto`;

      setMetaTag('og:title', activity.title);
      setMetaTag('og:description', t('sparing.ogDescription'));
      setMetaTag('og:image', 'https://spotto.pl/icon.png');
      setMetaTag('og:url', `https://spotto.pl/sparing/${activityId}`);
      setMetaTag('og:type', 'website');

      setMetaTag('twitter:card', 'summary_large_image', true);
      setMetaTag('twitter:title', activity.title, true);
      setMetaTag('twitter:description', t('sparing.ogDescription'), true);
      setMetaTag('twitter:image', 'https://spotto.pl/icon.png', true);

      setMetaTag('apple-itunes-app', `app-id=${APP_ID}, app-argument=${APP_SCHEME}://sparing/${activityId}`, true);
    }

    return () => {
      document.title = 'Spotto - Sport Facility Scheduler';
    };
  }, [activity, activityId, t]);

  useEffect(() => {
    fetchActivity();

    if (isMobile && !attempted) {
      tryOpenApp();
    }
  }, [activityId]);

  const fetchActivity = async () => {
    if (!activityId) return;

    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/GroupActivity/${activityId}`);
      if (!res.ok) {
        throw new Error('Group activity not found');
      }
      const data = await res.json();
      setActivity(data);
    } catch (err) {
      console.error('Failed to fetch group activity:', err);
      setError(t('sparing.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const tryOpenApp = () => {
    setAttempted(true);
    const appUrl = `${APP_SCHEME}://sparing/${activityId}`;

    window.location.href = appUrl;

    setTimeout(() => {
      window.location.href = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
    }, 2500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface-dark to-surface-dark-elevated flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="mb-8">
        <img src="/icon.png" alt="Spotto" className="w-20 h-20 rounded-full logo-glow" />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-ink-on-dark-muted text-center">
          <div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('sparing.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">😕</div>
          <p className="text-brand-warm mb-4">{error}</p>
        </div>
      )}

      {/* Activity Info Card */}
      {activity && !loading && (
        <div className="bg-surface-dark-elevated rounded-2xl p-6 max-w-md w-full shadow-xl border border-white/10 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-brand-primary rounded-xl flex items-center justify-center text-2xl text-ink-on-dark font-bold">
              {activity.sportType?.charAt(0).toUpperCase() || 'S'}
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold tracking-tight text-ink-on-dark">{activity.title}</h1>
              <p className="text-ink-on-dark-muted text-sm">{activity.sportType}</p>
            </div>
          </div>

          {activity.status === 'Cancelled' ? (
            <p className="text-brand-warm text-sm font-semibold">{t('sparing.cancelled')}</p>
          ) : (
            <div className="space-y-2 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-ink-on-dark-muted text-sm">{t('sparing.date')}</span>
                <span className="text-ink-on-dark">{formatDate(activity.date)}, {activity.startTime}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-ink-on-dark-muted text-sm">{t('sparing.location')}</span>
                <span className="text-ink-on-dark text-right">{activity.locationText}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-ink-on-dark-muted text-sm">{t('sparing.participants')}</span>
                <span className="text-ink-on-dark">{activity.confirmedCount}/{activity.maxParticipants}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Download Section */}
      <div className="text-center max-w-md">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-ink-on-dark mb-2">{t('sparing.downloadTitle')}</h2>
        <p className="text-ink-on-dark-muted mb-6">{t('sparing.downloadDescription')}</p>

        {/* Store Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-black hover:bg-surface-dark-elevated text-ink-on-dark px-6 py-3 rounded-xl transition-colors border border-white/10"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <div className="text-left">
              <div className="text-xs text-ink-on-dark-muted">{t('bookBusiness.downloadOn')}</div>
              <div className="text-lg font-semibold">App Store</div>
            </div>
          </a>

          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-black hover:bg-surface-dark-elevated text-ink-on-dark px-6 py-3 rounded-xl transition-colors border border-white/10"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            <div className="text-left">
              <div className="text-xs text-ink-on-dark-muted">{t('bookBusiness.getItOn')}</div>
              <div className="text-lg font-semibold">Google Play</div>
            </div>
          </a>
        </div>

        {/* Open in App Button (Mobile only) */}
        {isMobile && (
          <button
            onClick={tryOpenApp}
            className="w-full bg-brand-primary hover:bg-brand-primary-hover text-ink-on-dark font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            {t('sparing.openInApp')}
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 text-center text-ink-on-dark-muted text-sm">
        <p>© {new Date().getFullYear()} Spotto. {t('bookBusiness.allRightsReserved')}</p>
      </div>
    </div>
  );
};
